import type { ElementType, HTMLAttributes, ReactNode } from 'react';
import '../arc-foundation.css';
import { Dialog, DialogContent } from '../dialog/dialog';
import { CompactSelect } from '../compact-select/compact-select';
import { FilterToolbar } from '../filter-toolbar/filter-toolbar';
import { MetricCard } from '../metric-card/metric-card';
import { Select } from '../select/select';
import { Tabs, TabsList, TabsTrigger } from '../tabs/tabs';
import styles from './arc-primitives.module.css';

function join(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(' ');
}

export function ArcPage({
  as: Tag = 'section',
  className,
  children,
  ...rest
}: {
  as?: ElementType;
  className?: string;
  children: ReactNode;
} & HTMLAttributes<HTMLElement>) {
  return <Tag className={join('p1-arc-scope', styles.page, className)} {...rest}>{children}</Tag>;
}

export function ArcPageHeader({
  eyebrow,
  title,
  description,
  meta,
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  meta?: ReactNode;
  className?: string;
}) {
  return (
    <header className={join(styles.pageHeader, className)}>
      <div className={styles.pageHeaderCopy}>
        {eyebrow ? <div className={styles.eyebrow}>{eyebrow}</div> : null}
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {meta ? <div className={styles.pageHeaderMeta}>{meta}</div> : null}
    </header>
  );
}

export function ArcHeaderPeriodSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return <CompactSelect className={styles.headerPeriodSelect} value={value} onChange={onChange} options={options} ariaLabel="Období" />;
}

export interface ArcTabItem<T extends string> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}

export function ArcTabs<T extends string>({
  items,
  value,
  onChange,
  label,
  className,
}: {
  items: readonly ArcTabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <Tabs value={value} onValueChange={(next) => onChange(next as T)} className={join(styles.routeTabs, className)}>
      <TabsList aria-label={label}>
        {items.map((item) => (
          <TabsTrigger key={item.value} value={item.value} disabled={item.disabled}>{item.label}</TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

export function ArcToolbar({
  children,
  end,
  className,
  label,
}: {
  children: ReactNode;
  end?: ReactNode;
  className?: string;
  label?: string;
}) {
  return (
    <FilterToolbar
      className={join(styles.toolbarAdapter, className)}
      filters={[]}
      onRemove={() => undefined}
      label={label}
      leading={<div className={styles.toolbarMain}>{children}</div>}
    >
      {end ? <div className={`${styles.toolbarEnd} arc-toolbar-end`}>{end}</div> : null}
    </FilterToolbar>
  );
}

export function ArcSelect({
  label,
  value,
  onChange,
  options,
  className,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
  className?: string;
  disabled?: boolean;
}) {
  const emptyValue = '__p1_arc_empty__';
  return (
    <Select
      label={label}
      value={value || emptyValue}
      onValueChange={(next) => onChange(next === emptyValue ? '' : next)}
      options={options.map((option) => ({ ...option, value: option.value || emptyValue }))}
      disabled={disabled}
      className={join(styles.selectTrigger, className)}
      fieldClassName={styles.selectField}
    />
  );
}

export function ArcPanel({
  as: Tag = 'section',
  className,
  children,
  ...rest
}: {
  as?: ElementType;
  className?: string;
  children: ReactNode;
} & HTMLAttributes<HTMLElement>) {
  return <Tag className={join(styles.panel, className)} {...rest}>{children}</Tag>;
}

export function ArcSummaryGrid({ className, children }: { className?: string; children: ReactNode }) {
  return <section className={join(styles.summaryGrid, className)}>{children}</section>;
}

export function ArcSummaryMetric({
  label,
  value,
  context,
  change,
  suffix,
  density,
  className,
}: {
  label: string;
  value: number;
  context?: string;
  change?: string;
  suffix?: string;
  density?: 'default' | 'compact';
  className?: string;
}) {
  return (
    <div className={join(styles.summaryMetric, className)} data-density={density}>
      <MetricCard label={label} value={value} suffix={suffix} context={context} change={change} density={density} />
    </div>
  );
}

export function ArcTableFrame({
  children,
  className,
  label,
  maxHeight,
  sticky = 'viewport',
}: {
  children: ReactNode;
  className?: string;
  label?: string;
  maxHeight?: number | string;
  sticky?: 'viewport' | 'container';
}) {
  return (
    <div
      className={join(styles.tableFrame, className)}
      role="region"
      aria-label={label}
      data-sticky={sticky}
      tabIndex={0}
      style={maxHeight ? { maxHeight } : undefined}
    >
      {children}
    </div>
  );
}

export function ArcDialog({
  open = true,
  title,
  description,
  onClose,
  children,
  actions,
  size = 'large',
  className,
  bodyClassName,
}: {
  open?: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
  size?: 'medium' | 'large' | 'wide';
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent
        title={title}
        description={description}
        className={join(styles.arcDialogContent, styles[`arcDialog_${size}`], className)}
        bodyClassName={styles.arcDialogShellBody}
      >
        <div className={join(styles.arcDialogBody, bodyClassName)}>{children}</div>
        {actions ? <footer className={styles.arcDialogActions}>{actions}</footer> : null}
      </DialogContent>
    </Dialog>
  );
}
